import { useEffect, useState } from "react";
import { CATEGORIES, FEED_DETAILS, FEED_ITEMS } from "../../../mocks/gossip";
import type { FeedItem } from "../../../mocks/gossip";
import { CategoryChip, HotTag, SectionHead } from "./Shared";

export default function LatestSection() {
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState<FeedItem | null>(null);
  const [open, setOpen] = useState(false);

  const list = active === "all" ? FEED_ITEMS : FEED_ITEMS.filter((f) => f.categoryKey === active);

  const openItem = (item: FeedItem) => {
    setSelected(item);
    setOpen(true);
  };

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <section id="today" className="w-full py-10 md:py-14" aria-label="今日最新瓜料">
      <div className="container-x">
        <SectionHead
          eyebrow="TODAY&apos;S HOT"
          anchor="today"
          title="今日最新 · 一瓜都不能少"
          desc="按分类快速筛选今天的新瓜。想看谁塌房、谁翻车、还是谁又悄悄公开了恋情？点进卡片即可读到完整的事件进展。"
        />

        {/* filter chips */}
        <div className="scrollbar-hide -mx-1 mb-6 flex flex-wrap items-center gap-2 px-1">
          {CATEGORIES.map((c) => {
            const isActive = active === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setActive(c.key)}
                className={`flex cursor-pointer items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-200 bg-background-100/70 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {c.label}
                <span
                  className={`ml-1.5 text-[11px] font-bold ${
                    isActive ? "text-background-50/80" : "text-foreground-400"
                  }`}
                >
                  {c.key === "all"
                    ? FEED_ITEMS.length
                    : FEED_ITEMS.filter((f) => f.categoryKey === c.key).length}
                </span>
              </button>
            );
          })}
        </div>

        {/* feed grid */}
        {list.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
            <i className="ri-search-eye-line mx-auto block text-4xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-600">这个分类暂时没有新瓜，换个标签看看吧。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((item) => (
              <article
                key={item.id}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100/60 transition-all hover:-translate-y-1 hover:border-primary-200"
                onClick={() => openItem(item)}
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute left-3 top-3 flex items-center gap-2">
                    <CategoryChip label={item.category} toneKey={item.categoryKey} />
                    <span className="inline-flex items-center gap-1 rounded-full bg-background-50/85 px-2 py-0.5 text-[11px] font-semibold text-primary-600">
                      <i className="ri-time-line" />
                      {item.time}
                    </span>
                  </div>
                  <span className="absolute right-3 top-3">
                    <HotTag label={item.hot} tone={item.hot === "爆" ? "boom" : item.hot === "热" ? "hot" : "new"} />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{item.summary}</p>
                  <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {item.views} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line text-primary-500" />
                      {item.hot}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1 whitespace-nowrap font-semibold text-primary-600 opacity-0 transition-opacity group-hover:opacity-100">
                      展开全文 <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* detail modal */}
      {open && selected ? (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-foreground-950/60 p-4 backdrop-blur-sm md:items-center"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={selected.title}
        >
          <article
            className="my-4 w-full max-w-3xl overflow-hidden rounded-2xl bg-background-50 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-[16/8] w-full overflow-hidden">
              <img src={selected.image} alt={selected.title} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="关闭"
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-background-50/90 text-foreground-800 transition-colors hover:bg-background-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
              <div className="absolute bottom-3 left-3 flex items-center gap-2">
                <CategoryChip label={selected.category} toneKey={selected.categoryKey} />
                <span className="rounded-full bg-background-50/85 px-2 py-0.5 text-[11px] font-semibold text-foreground-700">
                  {selected.time} 更新
                </span>
              </div>
            </div>
            <div className="p-5 md:p-8">
              <h2 className="font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
                {selected.title}
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base">
                {selected.summary}
              </p>
              {(FEED_DETAILS[selected.id] || []).map((p, i) => (
                <p key={i} className="mt-3 text-sm leading-7 text-foreground-600 md:text-[15px]">
                  {p}
                </p>
              ))}
              <div className="mt-6 flex flex-col items-start gap-3 border-t border-background-200 pt-5 sm:flex-row sm:items-center">
                <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1"><i className="ri-eye-line" /> {selected.views} 阅读</span>
                  <span className="flex items-center gap-1 text-primary-600"><i className="ri-fire-line" /> {selected.hot}</span>
                </div>
                <div className="flex gap-2 sm:ml-auto">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="cursor-pointer whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    关闭
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      document.querySelector("#topics")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    查看更多热议
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>
      ) : null}
    </section>
  );
}